// ─── Side Effect Manager ──────────────────────────────────────────────────────
//
// Tracks runtime side effects that have already occurred during action execution.
// This is an audit log, NOT a command queue. Effects are recorded AFTER they
// complete. The sideEffectManager never drives execution - it observes it.
//
// Ownership boundary: collectionEngine and appStateStore mutations call
// record() after the operation succeeds. executionTracer snapshots the
// accumulated effects when recording a trace entry.

// ─── Types ────────────────────────────────────────────────────────────────────

export type SideEffectKind =
  | 'record_inserted'
  | 'record_updated'
  | 'record_deleted'
  | 'state_changed'
  | 'state_reset';

export interface SideEffectRecord {
  kind: SideEffectKind;
  /** Present for record_* effects */
  collection?: string;
  /** Present for record_* effects */
  recordId?: string;
  /** Present for state_changed */
  stateKey?: string;
  /** Present for state_reset */
  scope?: 'global' | 'page' | 'component';
}

// ─── Manager ──────────────────────────────────────────────────────────────────

class SideEffectManagerImpl {
  private effects: SideEffectRecord[] = [];

  /** Record a completed side effect. Called by collectionEngine/appStateStore wrappers. */
  record(effect: SideEffectRecord): void {
    this.effects.push(effect);
  }

  /**
   * Flush all recorded effects and return them.
   * Called by executionTracer when recording a trace entry.
   * Clears the internal buffer.
   */
  flush(): SideEffectRecord[] {
    const snapshot = [...this.effects];
    this.effects = [];
    return snapshot;
  }

  /** Inspect effects without clearing. */
  peek(): SideEffectRecord[] {
    return [...this.effects];
  }

  /** Total number of pending effects. */
  count(): number {
    return this.effects.length;
  }

  /** Clear effects without returning. For test isolation. */
  clear(): void {
    this.effects = [];
  }
}

export const sideEffectManager = new SideEffectManagerImpl();
